import { useEffect } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Link } from 'react-router-dom'
import { X, Maximize2, MessageSquarePlus } from 'lucide-react'
import AgentChat from './components/AgentChat'
import ConversationSwitcher from './components/ConversationSwitcher'
import { useAgentConversationStore, useAgentPanelStore } from './store/agentStore'

/**
 * Centered floating agent modal (Notion Q&A / Linear Ask AI style).
 * Sits over the current page so the user keeps full visual context.
 */
export default function AgentModal() {
    const isOpen = useAgentPanelStore(s => s.isOpen)
    const closePanel = useAgentPanelStore(s => s.closePanel)
    const togglePanel = useAgentPanelStore(s => s.togglePanel)

    const generalScope = 'general' as const
    const activeConversationId = useAgentConversationStore(s => s.slots.general.activeConversationId)
    const startDraft = () => useAgentConversationStore.getState().startDraftConversation(generalScope)
    const selectConversation = (id: string | null) =>
        useAgentConversationStore.getState().selectConversation(generalScope, id)

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const isToggle = (e.metaKey || e.ctrlKey) && (e.key === 'i' || e.key === 'I')
            if (isToggle) {
                e.preventDefault()
                togglePanel()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [togglePanel])

    return (
        <DialogPrimitive.Root open={isOpen} onOpenChange={open => (open ? null : closePanel())}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className='fixed inset-0 z-50 bg-slate-950/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
                />
                <DialogPrimitive.Content
                    onOpenAutoFocus={e => e.preventDefault()}
                    className='fixed left-[50%] top-[50%] z-50 flex h-[82vh] max-h-[780px] w-[94vw] max-w-3xl translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-[20px] bg-white ring-1 ring-slate-900/10 shadow-[0_0_0_1px_rgba(255,255,255,0.9),0_30px_80px_-20px_rgba(15,23,42,0.55),0_15px_40px_-15px_rgba(15,23,42,0.4),0_4px_12px_-4px_rgba(15,23,42,0.2)] duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
                >
                    <DialogPrimitive.Title className='sr-only'>Agent assistant</DialogPrimitive.Title>
                    <DialogPrimitive.Description className='sr-only'>Chat with the agent while keeping your current page context.</DialogPrimitive.Description>

                    <header className='flex items-center gap-1 border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/40 px-2.5 py-2'>
                        <div className='min-w-0 flex-1'>
                            <ConversationSwitcher
                                activeId={activeConversationId}
                                onSelect={selectConversation}
                                onNewChat={startDraft}
                            />
                        </div>

                        <button
                            type='button'
                            onClick={startDraft}
                            title='New chat'
                            className='inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                        >
                            <MessageSquarePlus className='h-3.5 w-3.5' />
                        </button>

                        <Link
                            to={activeConversationId ? `/dashboard/agent?c=${activeConversationId}` : '/dashboard/agent'}
                            onClick={closePanel}
                            title='Open in full view'
                            className='inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                        >
                            <Maximize2 className='h-3.5 w-3.5' />
                        </Link>

                        <DialogPrimitive.Close asChild>
                            <button
                                type='button'
                                title='Close (Esc)'
                                className='inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                            >
                                <X className='h-3.5 w-3.5' />
                            </button>
                        </DialogPrimitive.Close>
                    </header>

                    {isOpen && <AgentChat compact />}

                    <div className='pointer-events-none absolute bottom-2.5 right-3 flex items-center gap-1 text-[10px] font-medium text-slate-300'>
                        <kbd className='rounded border border-slate-200 bg-white px-1 py-0.5 font-mono'>Esc</kbd>
                        <span>to close</span>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    )
}
